import { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RichTextEditor } from './RichTextEditor';
import { convertGoogleDriveUrl, isGoogleDriveUrl } from '../../lib/imageUtils';

/**
 * UnifiedContentEditor - Blogger-style content editing layout
 * 
 * Provides a consistent editing experience across:
 * - Articles
 * - Initiatives
 * - Media Coverage
 * 
 * Layout:
 * - Main panel (left/center): Title + Rich-text body
 * - Side panel (right): Settings, metadata, publishing options
 */
export const UnifiedContentEditor = forwardRef(function UnifiedContentEditor({
  // Content type info
  contentType = 'article', // 'article' | 'initiative' | 'media'
  pageTitle = 'New Content',
  
  // Core content fields
  title = '',
  onTitleChange,
  titlePlaceholder = 'Enter title...',
  
  // Optional excerpt/description (for articles)
  excerpt = '',
  onExcerptChange,
  showExcerpt = false,
  excerptPlaceholder = 'Brief introduction...',
  excerptHelp = '',
  
  // Rich-text body content
  content = '',
  onContentChange,
  contentPlaceholder = 'Start writing your content here...',
  
  // Actions
  onBack,
  onSubmit,
  saving = false,
  isEditing = false,
  submitLabel = 'Save',
  
  // Side panel content (passed as children or specific props)
  sidebarContent,
  
  // Additional main content below editor (e.g., citations)
  additionalContent,
  
  // Featured image
  featuredImage = '',
  onFeaturedImageChange,
  showFeaturedImage = true,

  // Citation support
  citations = [],
  onInsertCitation,
  showCitationButton = false
}, ref) {
  const [imageError, setImageError] = useState(false);
  const [displayImageUrl, setDisplayImageUrl] = useState('');
  const editorRef = useRef(null);

  // Expose editor methods to parent
  useImperativeHandle(ref, () => ({
    insertCitation: (citationIndex) => {
      if (editorRef.current) {
        editorRef.current.insertCitation(citationIndex);
      }
    }
  }));

  // Convert Google Drive URLs and update display URL
  useEffect(() => {
    if (featuredImage) {
      const convertedUrl = convertGoogleDriveUrl(featuredImage);
      setDisplayImageUrl(convertedUrl);
      setImageError(false);
    } else {
      setDisplayImageUrl('');
      setImageError(false);
    }
  }, [featuredImage]);

  // Handle featured image URL change
  const handleFeaturedImageChange = (url) => {
    onFeaturedImageChange(url);
  };

  // Handle image load error - try fallback for Google Drive
  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="unified-content-editor" data-testid={`${contentType}-editor`}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          {pageTitle}
        </h1>
      </div>

      <form onSubmit={onSubmit}>
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Writing Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Writing Canvas Card */}
            <div className="bg-background border border-border rounded-lg shadow-sm">
              {/* Title Input - Large, prominent */}
              <div className="p-6 border-b border-border">
                <Input
                  value={title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  placeholder={titlePlaceholder}
                  className="text-2xl md:text-3xl font-heading font-bold border-0 p-0 h-auto focus-visible:ring-0 placeholder:text-muted-foreground/50"
                  required
                  data-testid={`${contentType}-title`}
                />
              </div>

              {/* Excerpt/Description (optional) */}
              {showExcerpt && (
                <div className="px-6 py-4 border-b border-border bg-muted/30">
                  <textarea
                    value={excerpt}
                    onChange={(e) => onExcerptChange(e.target.value)}
                    placeholder={excerptPlaceholder}
                    rows={2}
                    className="w-full text-base text-muted-foreground bg-transparent border-0 p-0 resize-none focus:outline-none placeholder:text-muted-foreground/50"
                    data-testid={`${contentType}-excerpt`}
                  />
                  {excerptHelp && (
                    <p className="text-xs text-muted-foreground mt-1">{excerptHelp}</p>
                  )}
                </div>
              )}

              {/* Rich Text Editor - Main Writing Area */}
              <div className="p-6">
                <RichTextEditor
                  ref={editorRef}
                  value={content}
                  onChange={onContentChange}
                  placeholder={contentPlaceholder}
                  minHeight="450px"
                  citations={citations}
                  onInsertCitation={onInsertCitation}
                  showCitationButton={showCitationButton}
                />
              </div>
            </div>

            {/* Additional Content (Citations, etc.) */}
            {additionalContent && (
              <div className="bg-background border border-border rounded-lg shadow-sm p-6">
                {additionalContent}
              </div>
            )}
          </div>

          {/* Side Panel - Settings & Metadata */}
          <div className="space-y-6">
            {/* Sidebar Content (Publishing, Categories, etc.) */}
            {sidebarContent}

            {/* Featured Image Section */}
            {showFeaturedImage && (
              <div className="bg-background border border-border rounded-lg shadow-sm p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Featured Image</Label>
                </div>
                <Input
                  value={featuredImage}
                  onChange={(e) => handleFeaturedImageChange(e.target.value)}
                  placeholder="https://..."
                  className="text-sm"
                  data-testid={`${contentType}-featured-image`}
                />
                <p className="text-xs text-muted-foreground">
                  Use a direct image URL. Google Drive links will be converted automatically.
                </p>
                
                {/* Image Preview */}
                {displayImageUrl && !imageError && (
                  <div className="relative aspect-video rounded overflow-hidden border border-border">
                    <img
                      src={displayImageUrl}
                      alt="Featured preview"
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </div>
                )}
                
                {/* Google Drive conversion notice */}
                {featuredImage && isGoogleDriveUrl(featuredImage) && displayImageUrl !== featuredImage && (
                  <p className="text-xs text-accent">
                    ✓ Google Drive link detected and converted for display.
                  </p>
                )}
                
                {/* Error state */}
                {imageError && (
                  <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                    Unable to load image. Please check the URL or try a different image host.
                  </div>
                )}
              </div>
            )}

            {/* Save Button */}
            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={saving}
              data-testid={`save-${contentType}`}
            >
              {saving ? (
                <span className="flex items-center">
                  <div className="spinner mr-2 h-4 w-4"></div>
                  Saving...
                </span>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {submitLabel}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
});

/**
 * SidebarCard - Reusable card for sidebar sections
 */
export function SidebarCard({ title, children, className = '' }) {
  return (
    <div className={`bg-background border border-border rounded-lg shadow-sm p-5 space-y-4 ${className}`}>
      {title && (
        <h3 className="font-heading text-sm font-semibold text-foreground uppercase tracking-wide">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

export default UnifiedContentEditor;
